// Sidebar.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, Snackbar, Button, Avatar, Stack } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import DispatchIcon from '@mui/icons-material/MinorCrash';
import FutureIcon from '@mui/icons-material/Update';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChatIcon from '@mui/icons-material/Chat';
import ChatDialog from './Dispatch/Itinerary/ChatDialog';

function Sidebar({
  open,
  onClose,
  activeComponent,
  userSettings: propUserSettings,
  fleetSettings: propFleetSettings,
  navigateToComponent,
  dispatchType,
  socket,
  selectedVehicle,
  routeDetails,
  chatOpen,
  onOpenChat,
  onCloseChat,
  pendingChatMessage,
  onPendingChatMessageSent,
}) {
  const [chatNotification, setChatNotification] = useState(null);

  // Tracks the timestamp after which any dispatch message is considered "new".
  // Session-only (useRef) so we never re-notify about messages from before this session.
  const lastSeenTimestampRef = useRef(Date.now());

  // Poll chat:getMessages for messages that arrived while the app was backgrounded.
  // Called on socket reconnect and when the page regains visibility.
  const checkForMissedMessages = useCallback(() => {
    if (!socket || !routeDetails?.routeId || chatOpen) return;

    socket.emit('chat:getMessages', { routeId: routeDetails.routeId }, (response) => {
      if (!Array.isArray(response)) return;

      const cutoff = lastSeenTimestampRef.current;
      lastSeenTimestampRef.current = Date.now();

      // Find dispatch messages (direction 'Out') newer than the last seen timestamp
      const missed = response.filter(msg => {
        if (msg.direction !== 'Out') return false;
        const ts = msg.timestamp ? new Date(msg.timestamp).getTime() : 0;
        return ts > cutoff;
      });

      if (missed.length === 0) return;

      // Surface the most recent missed message
      const latest = missed.sort((a, b) =>
        new Date(b.timestamp) - new Date(a.timestamp)
      )[0];

      setChatNotification({
        sender: latest.from || 'Dispatch',
        message: latest.message || latest.text || latest.data || ''
      });
    });
  }, [socket, routeDetails?.routeId, chatOpen]);

  // Trigger a missed-message check whenever the socket reconnects (app was backgrounded).
  useEffect(() => {
    if (!socket) return;
    socket.on('connect', checkForMissedMessages);
    return () => {
      socket.off('connect', checkForMissedMessages);
    };
  }, [socket, checkForMissedMessages]);

  // Trigger a missed-message check whenever the page becomes visible again.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForMissedMessages();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForMissedMessages]);

  // Listen for incoming chat messages when chat dialog is closed
  useEffect(() => {
    if (!socket || !routeDetails?.routeId) return;

    const handleNewMessage = (messageData) => {
      // Only show notification if chat dialog is closed and it's for our route
      if (!chatOpen && messageData.routeId === routeDetails.routeId) {
        setChatNotification({
          sender: messageData.senderName || 'Dispatch',
          message: messageData.message || ''
        });
      }
    };

    socket.on('chat:newMessage', handleNewMessage);

    return () => {
      socket.off('chat:newMessage', handleNewMessage);
    };
  }, [socket, routeDetails?.routeId, chatOpen]);

  const handleCloseNotification = useCallback(() => {
    setChatNotification(null);
  }, []);

  const handleOpenChat = useCallback(() => {
    setChatNotification(null);
    lastSeenTimestampRef.current = Date.now();
    onOpenChat();
  }, [onOpenChat]);

  const handleItemClick = (componentName) => {
    if (componentName === 'chat') {
      // Open chat dialog instead of navigating
      onOpenChat();
      onClose();
      return;
    }
    if (componentName === 'sos') {
      // Open the emergency confirm step (EmergencyAlarm listens for this).
      window.dispatchEvent(new CustomEvent('driverapp:open-sos'));
      onClose();
      return;
    }
    if (navigateToComponent) {
      navigateToComponent(componentName);
    }
    onClose();
  };

  // Show chat for itinerary, looped shuttle, and fixed shuttle dispatch modes
  const showChat = dispatchType === 'itinerary' ||
                   dispatchType === 'shuttlebus-looped' ||
                   dispatchType === 'shuttlebus-fixed';

  // Home / Schedule / Emergency SOS are configurable per fleet, not hardcoded —
  // each defaults to "on for ACET, off elsewhere" (matching the same
  // fleet-code fallback Dashboard.js uses for its own ACET-only features) but
  // any fleet's own config can override any of them independently via
  // fleetSettings.driverSidebarMenu, without a code change. Logout is
  // deliberately NOT gated — hiding it would leave drivers on other fleets
  // with no way to log out from the sidebar at all.
  const resolvedFleetCode = (propFleetSettings?.unitId || propFleetSettings?.fleetCode || localStorage.getItem('fleetCode') || '').toUpperCase();
  const isAcetFleet = resolvedFleetCode === 'ACET';
  const sidebarMenuConfig = propFleetSettings?.driverSidebarMenu || {};
  const showHome = sidebarMenuConfig.home ?? isAcetFleet;
  const showSchedule = sidebarMenuConfig.schedule ?? isAcetFleet;
  const showEmergencySos = sidebarMenuConfig.emergencySos ?? isAcetFleet;

  const menuItems = [
    {
      component: 'dashboard',
      text: 'Home',
      icon: <HomeIcon />,
      permission: showHome
    },
    {
      // Opens the dedicated 15-day "My Schedule" view, not the full Bookings screen
      // (Dashboard/Future) with its scope/status tabs. Bookings is still reachable
      // from the dashboard as before.
      component: 'Dashboard/MySchedule',
      text: 'Schedule',
      icon: <FutureIcon />,
      permission: showSchedule && propFleetSettings?.future
    },
    {
      component: 'dispatch',
      text: 'Dispatch',
      icon: <DispatchIcon />,
      permission: true // Always shown
    },
    {
      component: 'Dispatch/CompleteShift',
      text: 'End Shift',
      icon: <ExitToAppOutlinedIcon />,
      permission: true // Always shown
    },
    {
      component: 'logout',
      text: 'Logout',
      icon: <LogoutIcon />,
      permission: true // Always shown for every fleet — see note above
    },
    {
      component: 'chat',
      text: 'Chat with Dispatch',
      icon: <ChatIcon />,
      permission: showChat // Only shown in itinerary / shuttle modes
    },
    {
      component: 'sos',
      text: 'Emergency SOS',
      icon: <WarningAmberIcon />,
      permission: showEmergencySos,
      danger: true
    }
  ];

  return (
    <>
      <Drawer anchor="left" open={open} onClose={onClose}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2, mb: 1, px: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14 }}>
            {(propUserSettings?.firstName?.[0] || '') + (propUserSettings?.lastName?.[0] || '')}
          </Avatar>
          <Typography variant="h6" sx={{ mb: 0 }}>
            {propUserSettings?.lastName}, {propUserSettings?.firstName}
          </Typography>
        </Stack>
        <Divider />

        <List>
          {menuItems.map((item, index) => (
            item.permission && (
              <ListItemButton
                key={index}
                onClick={() => handleItemClick(item.component)}
                selected={activeComponent === item.component}
                sx={item.danger ? { color: 'error.main' } : undefined}
              >
                <ListItemIcon sx={item.danger ? { color: 'error.main' } : undefined}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={item.danger ? { fontWeight: 700 } : undefined}
                />
              </ListItemButton>
            )
          ))}
        </List>
      </Drawer>

      {/* Chat Dialog for itinerary / shuttle modes */}
      <ChatDialog
        isOpen={chatOpen}
        onClose={onCloseChat}
        socket={socket}
        routeId={routeDetails?.routeId}
        driverName={propUserSettings ? `${propUserSettings.firstName} ${propUserSettings.lastName}` : 'Driver'}
        pendingMessage={pendingChatMessage}
        onPendingMessageSent={onPendingChatMessageSent}
      />

      {/* Chat notification snackbar */}
      <Snackbar
        open={!!chatNotification}
        onClose={handleCloseNotification}
        message={chatNotification ? `${chatNotification.sender}: ${chatNotification.message}` : ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            fontSize: '1.5rem',
            padding: '16px 24px',
            '& .MuiSnackbarContent-message': {
              fontSize: '1.5rem',
              padding: '8px 0'
            },
            '& .MuiSnackbarContent-action': {
              paddingLeft: '24px'
            }
          }
        }}
        action={
          <>
            <Button
              color="primary"
              onClick={handleOpenChat}
              sx={{ fontSize: '1.25rem', padding: '8px 16px' }}
            >
              Open
            </Button>
            <Button
              color="inherit"
              onClick={handleCloseNotification}
              sx={{ fontSize: '1.25rem', padding: '8px 16px' }}
            >
              Dismiss
            </Button>
          </>
        }
      />
    </>
  );
}

export default Sidebar;
